// hook created to navigate to different routes

import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getCrmDashboardPath } from "../utils/crmRoutes";
import { getReturningDemoTarget } from "../utils/demoMembership";

const useNavigateTo = () => {
  const navigate = useNavigate();

  const getNavigation = useCallback((user) => {
    // Handle case where only role string is passed (backward compatibility)
    const role = typeof user === 'string' ? user : user?.role;
    const status = user?.status;

    // ── Invited seat users: always go to CRM dashboard, never pricing ──
    const isInvitedSeat = user?.isInvitedSeat || !!user?.parentUserId;
    if (isInvitedSeat) {
      const category = user?.plan?.category || user?.planCategory || "real_estate";
      navigate(getCrmDashboardPath(category));
      return;
    }

    // ── Returning demo-gate signup ────────────────────────────────────
    // Someone who signed up to take a demo, logged out, and is now
    // signing back in. Their account is planless (status:"disabled"),
    // so without this they'd be dumped on /pricing as if they were a
    // brand-new organic signup — losing the demo they were promised.
    // Send them back into their tour instead; if the window has
    // elapsed, the demo still opens and DemoBanner overlays the
    // "demo expired — pick a plan" gate. Skipped once they hold a real
    // plan (getReturningDemoTarget returns null), so converted users
    // fall through to their CRM below. Admins/affiliates never match.
    if (role !== "admin" && role !== "affiliate") {
      const demoTarget = getReturningDemoTarget(user);
      if (demoTarget) return navigate(demoTarget);
    }

    if (status === "disabled") return navigate("/pricing");

    switch (role) {
      case "admin":
        navigate("/admin/dashboard");
        break;
      case "affiliate":
        navigate("/affiliate/dashboard");
        break;
      case "real_estate":
      case "others": {
        // Route to the correct CRM based on plan category
        const category = user?.plan?.category || user?.planCategory || null;
        if (category) {
          navigate(getCrmDashboardPath(category));
        } else {
          // No plan category yet — send to pricing to pick one
          navigate("/pricing");
        }
        break;
      }
      default:
        navigate("/pricing");
    }
  }, [navigate]);

  return {
    getNavigation,
  };
};

export default useNavigateTo;
